import { Box, Button, Grid, Typography, useTheme } from "@mui/material";
import { tokens } from "../../../../../theme";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import type { RootState } from "../../../../../store/Index";
import { useSelector } from "react-redux";
import { MemoryBook } from "../../../../../types/memory-table";
import { User } from "../../../../users-admin/types/User";
import { toast } from "react-toastify";
import { showErrorToast } from "../../../../../components/common/ShowToast";
import PropTypes from "prop-types";

interface Props {
  memoryBooks: MemoryBook[];
  isLoading: boolean;
  error: boolean;
  uploadUser: User | null;
  isAdmin: boolean;
}

export const CurrentMemoryBookInfo: React.FC<Props> = ({
  memoryBooks,
  isLoading,
  error,
  uploadUser: _uploadUser,
  isAdmin,
}) => {
  void _uploadUser;
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const determineUploadedName = (): string | undefined => {
    const currentBook = memoryBooks[0];

    if (
      currentBook.created_by.first_name.length === 0 ||
      currentBook.created_by.last_name.length === 0
    ) {
      return currentBook.created_by.username;
    }

    return `${currentBook.created_by.first_name} ${currentBook.created_by.last_name}`;
  };

  const downloadCurrentMemoryBook = async () => {
    try {
      const url = memoryBooks[0].file;

      fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `memory_book_${memoryBooks[0].id}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(a.href);
        })
        .catch((error) => {
          console.error("Failed to download memory book:", error);
          showErrorToast("Failed to download memory book. Please try again.");
        });

      /*  const a = document.createElement("a");
      a.href = url;
      a.download = `memory_book_${memoryBooks[0].id}.pdf`; // suggest a filename (optional)
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a); */
    } catch (err: unknown) {
      console.error("Failed to download memory book:", err);
      toast.error("Failed to download memory book.Please try again", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    }
  };

  const viewBook = () => {
    window.open(memoryBooks[0].file, "_blank", "noopener,noreferrer");
  };

  return (
    <Box padding={1} bgcolor={colors.primary[800]} mb={2}>
      <Typography variant="body1" mb={1} fontWeight={500}>
        Current File
      </Typography>

      {!isLoading && error && (
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          flexDirection={"column"}
        >
          <DescriptionIcon
            sx={{ color: colors.primary[300], mb: 2 }}
          />
          <Typography variant="body1">
            Cannot display current file at the moment
          </Typography>
        </Box>
      )}

      {!isLoading && !error && memoryBooks.length === 0 && (
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          flexDirection={"column"}
        >
          <DescriptionIcon
            sx={{ color: colors.primary[300], mb: 2 }}
          />
          <Typography variant="body1">
            No memory book available at the moment
          </Typography>
        </Box>
      )}

      {!isLoading && !error && memoryBooks.length > 0 && (
        <Grid container justifyContent={"space-between"}>
          <Grid display={"flex"} alignItems={"center"}>
            <DescriptionIcon sx={{ color: colors.primary[300] }} />
            <Box ml={1}>
              <Typography variant="body1" fontWeight={600}>
                {memoryBooks[0].file.split("/").at(-1)}
              </Typography>
              {isAdmin && (
                <Typography variant="body1" fontSize={10}>
                  {determineUploadedName()}
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid>
            <Button variant="contained" onClick={downloadCurrentMemoryBook}>
              <DownloadIcon />
              Download
            </Button>

            <Button onClick={viewBook}>
              <FullscreenIcon />
              View Book
            </Button>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

CurrentMemoryBookInfo.propTypes = {
  memoryBooks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      file: PropTypes.string.isRequired,
      created_by: PropTypes.shape({
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        username: PropTypes.string,
      }).isRequired,
    })
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  uploadUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  isAdmin: PropTypes.bool.isRequired,
};
